'use client';

import {useState, useRef} from 'react';

import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiTypography from '@new-components/atoms/MuiTypography';
import FeaturedCommunityCarousel from '@new-components/organisms/UpcomingEventsCarousel';
import MuiIconButton from '@new-components/atoms/MuiIconButton';

import {CssVariables, MediaQuery} from '@constants/theme';

import {
  NextArrow,
  PrevArrow,
} from '@new-components/templates/HomeFeaturedCommunities/icons';

import {
  IsEnabledType,
  UpcomingEventsCarouselRef,
  UpcomingEventsTypesProps,
} from './types';

const CommunityUpcomingEvents = ({
  title,
  description,
  events,
}: UpcomingEventsTypesProps): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState<IsEnabledType>({
    prev: true,
    next: true,
  });
  const sliderRef = useRef<UpcomingEventsCarouselRef>(null);

  const handleNext = () => {
    if (sliderRef.current && sliderRef.current.next) {
      sliderRef.current.next();
    }
  };
  const handlePrev = () => {
    if (sliderRef.current && sliderRef.current.prev) {
      sliderRef.current.prev();
    }
  };

  return (
    <MuiContainer
      style={{paddingLeft: '0', paddingRight: '0'}}
      sx={{
        padding: 0,
        paddingTop: 4,
        paddingBottom: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        [MediaQuery.desktop]: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      }}>
      <MuiBox
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: `0 var(${CssVariables.mobilePadding})`,
        }}>
        <MuiBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
          <MuiTypography
            sx={{
              color: '#2D2D2D',
              fontSize: '24px',
              fontWeight: 400,
              lineHeight: '125%',
              letterSpacing: '-0.4px',
              [MediaQuery.desktop]: {
                fontSize: '48px',
                fontWeight: 300,
                lineHeight: '110%',
                letterSpacing: '-0.8px',
              },
            }}>
            {title}
          </MuiTypography>
          <MuiTypography
            sx={{
              fontSize: '15px',
              lineHeight: '18.75px',
              fontWeight: 300,
              letterSpacing: '-0.4px',
              color: '#2D2D2D',
            }}>
            {description}
          </MuiTypography>
        </MuiBox>
        {events.length > 4 && (
          <MuiBox
            sx={{
              display: 'none',
              gap: 2,
              [MediaQuery.desktop]: {display: 'flex'},
            }}>
            <MuiIconButton
              disabled={isEnabled.prev}
              onClick={handlePrev}
              data-testid='fcc-prev-button'
              sx={{
                'width': '40px',
                'height': '40px',
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
                'cursor': 'pointer',
              }}>
              <PrevArrow />
            </MuiIconButton>
            <MuiIconButton
              disabled={isEnabled.next}
              onClick={handleNext}
              data-testid='fcc-next-button'
              sx={{
                'width': '40px',
                'height': '40px',
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
                'cursor': 'pointer',
              }}>
              <NextArrow />
            </MuiIconButton>
          </MuiBox>
        )}
      </MuiBox>
      <FeaturedCommunityCarousel
        ref={sliderRef}
        onEnableChange={setIsEnabled}
        eventList={events}
      />
    </MuiContainer>
  );
};

export default CommunityUpcomingEvents;
